import { useEffect, useRef, useState, type DependencyList, type RefObject } from 'react';

/**
 * 스크롤 박스(표·목록)를 **화면 높이 안에 딱 맞춘다** — 박스 안만 움직이고 페이지(틀)는 안 움직이게(#255).
 *
 * 높이 = 화면 높이(`--app-h`) − 박스가 시작하는 자리 − **박스 아래에 붙은 것**(쪽 넘김·합계·버튼 줄) − 여백.
 * 🔴 왜: 예전엔 표마다 「아래 여백 1.5rem」·「100vh−14rem」 같은 추정값을 썼다. 표 밑의 「전체/이전/다음」 줄이
 *    그 추정에 안 들어가 페이지가 그만큼 넘쳤고, 쪽을 넘기려고 내리면 제목·검색 줄까지 같이 밀려 올라갔다.
 *    아래에 붙은 것은 **실제 높이를 재서** 뺀다(화면마다 다르다).
 *
 * - 박스 아래 내용이 `STACK_PX` 보다 크면(표 두 개를 위아래로 쌓은 화면) 예전처럼 빼지 않는다 —
 *   빼면 위 표가 납작해진다. 그런 화면은 원래 페이지를 내려 아래 표를 본다.
 * - 숨은 탭(keep-alive)에서는 재지 않고, 다시 보일 때 크기 변화로 다시 잰다.
 * - `min` 아래로는 줄이지 않는다(낮은 노트북 화면에서 표가 몇 줄만 남지 않게 — 그땐 페이지가 조금 내려간다).
 */
const STACK_PX = 200;

function belowContent(el: HTMLElement): number {
  const boxBottom = el.getBoundingClientRect().bottom;
  let bottom = boxBottom;
  // 아래에 「표와 같이 한 화면에 있어야 하는」 상자(`data-fit-below` — 운행차량편성 아래 내역 탭 #300)가 있으면
  // 크기와 무관하게 뺀다 — 안 빼면 그 상자가 페이지 아래로 밀려 창 전체가 스크롤된다.
  let forced = false;
  for (let node: HTMLElement = el; ; ) {
    // 🔴 sticky 칸(배차 왼쪽 접수 목록)은 그 칸 안만 본다 — 칸 밖은 옆 칸(오른쪽 상세)이라 「아래」가 아니다(#301).
    if (node !== el && getComputedStyle(node).position === 'sticky') break;
    const parent = node.parentElement;
    if (!parent || parent === document.body || parent.classList.contains('app-main')) break;
    const pcs = getComputedStyle(parent);
    // 모달·패널처럼 제 스크롤을 가진 상자 안이면 그 상자가 기준이다 — 여기서 멈춘다.
    if (/(auto|scroll)/.test(pcs.overflowY)) break;
    for (let sib = node.nextElementSibling; sib; sib = sib.nextElementSibling) {
      const cs = getComputedStyle(sib);
      if (cs.display === 'none' || cs.position === 'fixed' || cs.position === 'absolute') continue;
      const r = sib.getBoundingClientRect();
      if (r.height === 0) continue;
      // 🔴 박스 바닥보다 위에서 시작하는 형제 = 옆에 나란히 선 칸(그리드·flex 행). 「아래 붙은 것」이 아니다 —
      //    세면 그 칸 높이와 박스 높이가 서로를 바꿔 목록이 쉬지 않고 떨렸다(#301 최우석 영상 · 배차 왼쪽 목록).
      if (r.top < boxBottom - 1) continue;
      if (sib.hasAttribute('data-fit-below')) forced = true;
      bottom = Math.max(bottom, r.bottom + (parseFloat(cs.marginBottom) || 0));
    }
    bottom += (parseFloat(pcs.paddingBottom) || 0) + (parseFloat(pcs.borderBottomWidth) || 0);
    node = parent;
  }
  const below = Math.max(0, Math.round(bottom - boxBottom));
  return below > STACK_PX && !forced ? 0 : below;
}

function stickyAncestor(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    if (p.classList.contains('app-main')) return null;
    if (getComputedStyle(p).position === 'sticky') return p;
  }
  return null;
}

export function useFitHeight(
  ref: RefObject<HTMLElement | null>,
  deps: DependencyList = [],
  { min = '16rem', gap = '0.75rem' }: { min?: string; gap?: string } = {},
): string | undefined {
  const [maxHeight, setMaxHeight] = useState<string | undefined>(undefined);
  // 박스는 데이터가 온 뒤에야 그려지는 화면이 있다 → 렌더마다 「박스가 바뀌었나」만 보고 그때 붙인다(싸다).
  const bound = useRef<{ el: HTMLElement | null; measure?: () => void; off?: () => void }>({ el: null });
  useEffect(() => {
    const el = ref.current;
    if (el === bound.current.el) return;
    bound.current.off?.();
    bound.current = { el };
    if (!el) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      if (el.getClientRects().length === 0) return; // 숨은 탭
      // 🔴 sticky 칸 안이면(배차 왼쪽 접수 목록) 문서 기준 시작 자리가 스크롤마다 바뀐다 → 높이 → 페이지 길이 → 스크롤
      //    되먹임으로 목록 아래가 떨렸다(10-15 way). 그땐 「붙는 자리(top) + 칸 안 위치」로 — 스크롤과 무관하다.
      const sticky = stickyAncestor(el);
      const docTop = sticky
        ? Math.round((parseFloat(getComputedStyle(sticky).top) || 0) + el.getBoundingClientRect().top - sticky.getBoundingClientRect().top)
        : Math.round(el.getBoundingClientRect().top + window.scrollY);
      const below = belowContent(el);
      setMaxHeight(`max(${min}, calc(var(--app-h) - ${docTop + below}px - ${gap}))`);
    };
    // 숨은 브라우저 탭에선 requestAnimationFrame 이 안 돈다 — 그땐 짧은 타이머로(돌아왔을 때 옛 높이로 남지 않게).
    const later = () => {
      if (raf) return;
      raf = document.hidden ? (setTimeout(measure, 50) as unknown as number) : requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', later);
    // 위·아래 내용이 바뀌면(검색 줄 줄바꿈·쪽 넘김 줄이 늦게 뜸·탭이 다시 보임) 조상 크기가 바뀐다 → 다시 잰다.
    // 박스 자신의 높이는 잰 값(시작 자리·아래 내용)에 안 들어가므로 되먹임이 없다.
    const ro = new ResizeObserver(later);
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
      ro.observe(p);
      if (p.classList.contains('app-main')) break;
    }
    // 🔴 본문 **위**에 늦게 붙는 띠(계산서 요청 빨간 띠·편성 변경 띠)는 조상 크기가 아니라 **자리**만 밀어
    //    위 관찰에 안 걸린다 — 표가 처음 잰 높이대로 남아 페이지가 띠만큼 넘쳤다(10-15 #280 실측 828 창에 966).
    //    문서 높이가 바뀌면 다시 잰다(잰 값이 같으면 상태가 안 바뀌어 되먹임이 없다).
    ro.observe(document.documentElement);
    ro.observe(document.body);
    bound.current.measure = later;
    bound.current.off = () => {
      window.removeEventListener('resize', later);
      ro.disconnect();
      if (raf) { cancelAnimationFrame(raf); clearTimeout(raf); }
    };
  });
  useEffect(() => () => { bound.current.off?.(); bound.current = { el: null }; }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { bound.current.measure?.(); }, deps);
  return maxHeight;
}
